import React from 'react';
import * as ReactGA from 'react-ga';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import createSagaMiddleware from 'redux-saga';
import { all } from 'redux-saga/effects';
import freeze from 'redux-freeze';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { BrowserRouter as Router, Redirect, Route, Link, Switch } from 'react-router-dom';
import { MenuItem, NavDropdown } from 'react-bootstrap';
import { authorization, currentUser, legit, session } from 'apex-legit-service';
import FaSearch from 'react-icons/lib/fa/search';
import FaRefresh from 'react-icons/lib/fa/refresh';
import { apexMenuSagas } from 'apex-online-menu';
import { messages, ROUTER_BASENAME } from 'common';
import { getAppConfigSetting, loadIntlMessages } from 'utils';
import App, { appFlow } from 'containers/App';
import Search, { title as searchTitle } from 'containers/Search';
import Details from 'containers/Details';
import { sagas as searchServiceSagas } from 'services/search';
import { sagas as actionsServiceSagas } from 'services/actions';
import { sagas as recentDepositServiceSagas } from 'services/recentDeposits';
import { sagas as detailsServiceSagas } from 'services/details';
import { sagas as updateWireServiceSagas } from 'services/updateWire';
import { sagas as suspendWireServiceSagas } from 'services/suspendWire';
import { sagas as atlasRequestSagas } from 'services/atlasRequest';
import { sagas as addressRequestSagas } from 'services/addressPreview';
import idpCheckFlow from 'services/LoginType/sagas';
import ssoLoginFlow from 'services/SsoLogin/sagas';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-toggle/dist/bootstrap2-toggle.css';
import configureStore from './configureStore';
import './styles/main.global.css';
import { startRum } from './utils/datadog';

const middlewares = [freeze];

const sagaMiddleware = createSagaMiddleware();

middlewares.push(sagaMiddleware);

//create the redux store
const store = configureStore(middlewares);

//all sagas are run from the rootSaga
function* rootSaga() {
  const theSagas = [
    authorization.authorizeFlow(),
    currentUser.currentUserFlow(),
    legit.adoptExistingSessionFlow(),
    legit.loginFlow(),
    legit.logoutFlow(),
    session.createSessionFlow(),
    session.destroySessionFlow(),
    appFlow(),
    ...actionsServiceSagas,
    ...searchServiceSagas,
    ...recentDepositServiceSagas,
    ...detailsServiceSagas,
    ...updateWireServiceSagas,
    ...suspendWireServiceSagas,
    ...idpCheckFlow,
    ...atlasRequestSagas,
    ...addressRequestSagas,
    ssoLoginFlow(),
    ...apexMenuSagas(),
  ];

  //run child sagas in parallel
  yield all(theSagas);
}

//run the root saga
sagaMiddleware.run(rootSaga);

ReactGA.initialize(getAppConfigSetting('gaTrackingId'));

startRum();

document.addEventListener('DOMContentLoaded', () => {
  const links = [
    <a href="/sentinel/#/search">
      <FaSearch /> <FormattedMessage {...searchTitle} />
    </a>,
    <a href={getAppConfigSetting('processNavUrl', '')} target="_self">
      <FaRefresh /> <FormattedMessage {...messages.processNavTitle} />
    </a>,
    <NavDropdown id="reports-nav-menu" key="reports-nav-menu" title="Reports" placeholder="--">
      <MenuItem eventKey="achTotals" href={getAppConfigSetting('reportsAchTotalsNavUrl')}>
        <FormattedMessage {...messages.achTotals} />
      </MenuItem>
      <MenuItem eventKey="nachaReturns" href={getAppConfigSetting('reportsNachaReturnsNavUrl')}>
        <FormattedMessage {...messages.nachaReturns} />
      </MenuItem>
    </NavDropdown>,
    <Link to="/search">
      <FormattedMessage {...messages.searchAndProcessS2} />
    </Link>,
  ];

  const getActiveLink = (indices, link, i) => {
    const location = typeof window !== 'undefined' ? window.location.pathname : '';
    const isSearchPage = (location === ROUTER_BASENAME || location === '/') && link.props.to === '/search';
    if (location === `${ROUTER_BASENAME}${link.props.to}` || isSearchPage) {
      indices.push(i);
    }
    return indices;
  };

  const isActiveLink = (index) => index === links.reduce(getActiveLink, []).shift();

  render(
    <Provider store={store}>
      <IntlProvider {...loadIntlMessages()}>
        <Router basename={ROUTER_BASENAME}>
          <Route
            render={({ location }) => (
              <App
                isActiveLink={isActiveLink}
                links={links}
                location={location}
                render={() => (
                  <Switch>
                    <Route exact path="/" render={() => <Redirect to="/search" />} />
                    <Route path="/search" component={Search} />
                    <Route exact path="/details/:id" component={Details} />
                    <Route component={Search} />
                  </Switch>
                )}
              />
            )}
          />
        </Router>
      </IntlProvider>
    </Provider>,
    document.getElementById('root'),
  );
});



// WEBPACK FOOTER //
// ./src/index.js